// =============================================================================
// ESPOLIDAR WEB ADMIN - EVENTS PAGE
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react'
import { useEvents } from '../hooks/useEvents'
import { useDevices } from '../hooks/useDevices'
import { wsService } from '../services/websocket'
import {
  Activity,
  Radio,
  Radar,
  Cpu,
  Play,
  Image as ImageIcon,
  Volume2,
  Lightbulb,
  Zap,
  RefreshCw,
  Filter,
  Clock,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import type { EventWithTriggers, WSMessage } from '../types'
import { useQueryClient } from '@tanstack/react-query'

const eventTypeLabels: Record<string, { label: string; icon: typeof Radio; color: string }> = {
  radar_enter: { label: 'Radar Ingresso', icon: Radar, color: 'bg-blue-100 text-blue-700' },
  radar_exit: { label: 'Radar Uscita', icon: Radar, color: 'bg-blue-50 text-blue-500' },
  lidar_enter: { label: 'LIDAR Ingresso', icon: Radio, color: 'bg-green-100 text-green-700' },
  lidar_exit: { label: 'LIDAR Uscita', icon: Radio, color: 'bg-green-50 text-green-500' },
  heartbeat: { label: 'Heartbeat', icon: Activity, color: 'bg-gray-100 text-gray-500' },
  status: { label: 'Status', icon: Activity, color: 'bg-gray-100 text-gray-500' },
}

const actionTypeLabels: Record<string, { label: string; icon: typeof Play }> = {
  play_video: { label: 'Play Video', icon: Play },
  show_image: { label: 'Mostra Immagine', icon: ImageIcon },
  play_audio: { label: 'Play Audio', icon: Volume2 },
  led_12v_on: { label: 'LED 12V ON', icon: Lightbulb },
  led_12v_off: { label: 'LED 12V OFF', icon: Lightbulb },
  led_zone: { label: 'LED Zona', icon: Zap },
  led_clear: { label: 'LED Clear', icon: Zap },
}

export default function EventsPage() {
  const queryClient = useQueryClient()
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('')
  const [deviceFilter, setDeviceFilter] = useState<string>('')
  const [limit, setLimit] = useState(50)
  const [realtimeEvents, setRealtimeEvents] = useState<EventWithTriggers[]>([])
  const [hasMore, setHasMore] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const { data: events, isLoading, refetch, isFetching } = useEvents({
    event_type: eventTypeFilter || undefined,
    device_id: deviceFilter || undefined,
    limit,
  })

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || isFetching || !hasMore) return

    const { scrollTop, scrollHeight, clientHeight } = container
    if (scrollHeight - scrollTop - clientHeight < 200) {
      setLimit(prev => prev + 50)
    }
  }, [isFetching, hasMore])

  // Check if there are more events
  useEffect(() => {
    if (events && events.length < limit) {
      setHasMore(false)
    } else {
      setHasMore(true)
    }
  }, [events, limit])

  const { data: devices } = useDevices()

  // WebSocket per eventi real-time
  useEffect(() => {
    const unsubscribe = wsService.subscribe((message: WSMessage) => {
      if (message.type === 'device_event') {
        // Aggiungi evento in cima alla lista real-time
        const newEvent: EventWithTriggers = {
          id: Date.now(), // ID temporaneo
          event_type: (message.data as any)?.event_type || 'unknown',
          event_data: (message.data as any)?.data || null,
          timestamp: new Date().toISOString(),
          device_id: message.device_id || '',
          device_name: devices?.find(d => d.device_id === message.device_id)?.name || message.device_id || '',
          matched_triggers: [], // I trigger matchati arriveranno con il prossimo refresh
        }

        setRealtimeEvents(prev => [newEvent, ...prev.slice(0, 9)]) // Mantieni ultimi 10

        // Refresh eventi dal server per avere i trigger matchati
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['events'] })
        }, 500)
      }
    })

    return () => unsubscribe()
  }, [devices, queryClient])

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }


  // Combina eventi real-time con quelli dal server (rimuovi duplicati)
  const allEvents = [...realtimeEvents, ...(events || [])].reduce((acc, event) => {
    // Rimuovi duplicati basati su timestamp simile e device_id
    const isDuplicate = acc.some(e =>
      e.device_id === event.device_id &&
      e.event_type === event.event_type &&
      Math.abs(new Date(e.timestamp).getTime() - new Date(event.timestamp).getTime()) < 2000
    )
    if (!isDuplicate) acc.push(event)
    return acc
  }, [] as EventWithTriggers[])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Header fisso */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="text-primary-600" />
            Eventi e Trigger
          </h1>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary flex items-center gap-2"
            disabled={isFetching}
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            Aggiorna
          </button>
        </div>

        {/* Filtri */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filtri:</span>
            </div>

            <select
              value={eventTypeFilter}
              onChange={(e) => {
                setEventTypeFilter(e.target.value)
                setLimit(50)
              }}
              className="input text-sm py-1.5"
            >
              <option value="">Tutti gli eventi</option>
              <option value="radar_enter">Radar Ingresso</option>
              <option value="radar_exit">Radar Uscita</option>
              <option value="lidar_enter">LIDAR Ingresso</option>
              <option value="lidar_exit">LIDAR Uscita</option>
            </select>

            <select
              value={deviceFilter}
              onChange={(e) => {
                setDeviceFilter(e.target.value)
                setLimit(50)
              }}
              className="input text-sm py-1.5"
            >
              <option value="">Tutti i dispositivi</option>
              {devices?.map((device) => (
                <option key={device.device_id} value={device.device_id}>
                  {device.name}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{allEvents.length} eventi</span>
              {hasMore && <span className="text-gray-400">• scorri per altri</span>}
            </div>

            {realtimeEvents.length > 0 && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {realtimeEvents.length} nuovi
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Lista Eventi con scroll */}
      {allEvents && allEvents.length > 0 ? (
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto space-y-3 pr-2"
        >
          {allEvents.map((event, index) => {
            const eventInfo = eventTypeLabels[event.event_type] || {
              label: event.event_type,
              icon: Activity,
              color: 'bg-gray-100 text-gray-600',
            }
            const EventIcon = eventInfo.icon
            const isNew = index < realtimeEvents.length

            return (
              <div
                key={`${event.id}-${event.timestamp}`}
                className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${
                  isNew ? 'border-l-green-500 bg-green-50/30' : 'border-l-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Info Evento */}
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${eventInfo.color}`}>
                      <EventIcon size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${eventInfo.color}`}>
                          {eventInfo.label}
                        </span>
                        {!!(event.event_data as Record<string, unknown>)?.zone && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            Zona {String((event.event_data as Record<string, unknown>).zone)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Cpu size={14} />
                        <span className="font-medium">{event.device_name}</span>
                        <span className="text-gray-300">|</span>
                        <Clock size={14} />
                        <span>{formatTimestamp(event.timestamp)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Trigger Attivati */}
                  <div className="flex-shrink-0">
                    {event.matched_triggers && event.matched_triggers.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-gray-500 font-medium mb-1">
                          Trigger attivati:
                        </span>
                        {event.matched_triggers.map((trigger) => {
                          const actionInfo = actionTypeLabels[trigger.action_type] || {
                            label: trigger.action_type,
                            icon: Zap,
                          }
                          const ActionIcon = actionInfo.icon
                          return (
                            <div
                              key={trigger.id}
                              className="flex items-center gap-2 bg-primary-50 text-primary-700 px-2 py-1 rounded text-xs"
                            >
                              <ActionIcon size={12} />
                              <span>{actionInfo.label}</span>
                              <span className="text-primary-400">P:{trigger.priority}</span>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        Nessun trigger
                      </span>
                    )}
                  </div>
                </div>

                {/* Dettagli evento (se presenti) */}
                {event.event_data && Object.keys(event.event_data).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <details className="text-xs">
                      <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                        Dettagli evento
                      </summary>
                      <pre className="mt-2 p-2 bg-gray-50 rounded text-gray-600 overflow-x-auto">
                        {JSON.stringify(event.event_data, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            )
          })}

          {/* Loading indicator per infinite scroll */}
          {isFetching && (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-sm text-gray-500">Caricamento...</span>
            </div>
          )}

          {/* Fine lista */}
          {!hasMore && allEvents.length > 0 && (
            <div className="text-center py-4 text-sm text-gray-400">
              — Fine degli eventi —
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun evento
            </h3>
            <p className="text-gray-500">
              Gli eventi appariranno qui quando i dispositivi invieranno dati.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
